import { createInertiaApp } from '@inertiajs/react'
import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { resolvePage } from '../lib/page_registry'
import { refreshMarksAfterRestore } from '../lib/restored_marks'
import RiffrecProvider, { type RiffrecConfig } from '../lib/riffrec_provider'

// Once for the document: a signed-in reader's marks on the map are asked for
// again after Back or Forward to a map page (lib/restored_marks.ts).
refreshMarksAfterRestore()

void createInertiaApp({
  // Resolve page components from app/frontend/pages using the snake_case
  // "controller/action" identifier Rails passes to `render inertia:`. A
  // resolver of our own rather than the plugin's `pages: '../pages'`, because
  // that one cannot leave the test files out (see lib/page_registry.ts).
  resolve: resolvePage,

  // The bar is the one indicator a navigation gets on the map, so it wears the
  // accent rather than Inertia's default `#29d` blue, which belonged to no
  // surface of this app. `delay` keeps it off a visit that answers within a
  // frame or two; the spinner is off because the bar already says it. Partial
  // reloads (a pan, a keystroke) are async visits and never show it at all.
  progress: { color: '#7f1d3f', delay: 120, showSpinner: false },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: 'brackets' }
    },
  },

  // Branch CSR vs SSR from a single entrypoint: when the server pre-rendered the
  // markup it stamps `data-server-rendered="true"` on the root element, so we
  // hydrate instead of mounting fresh. Turning SSR on later (see
  // config/initializers/inertia_rails.rb) touches no code here.
  setup({ el, App, props }) {
    if (!el) return

    // Feedback-capture config is a static server setting, read once from the
    // initial page's shared props (not usePage, so this wrapper sits above App).
    const shared = props.initialPage.props as {
      feedback_capture_enabled?: boolean
      riffrec?: RiffrecConfig | null
    }

    const app = (
      <StrictMode>
        <RiffrecProvider
          enabled={Boolean(shared.feedback_capture_enabled)}
          config={shared.riffrec ?? null}
        >
          <App {...props} />
        </RiffrecProvider>
      </StrictMode>
    )

    if (el.dataset.serverRendered === 'true') {
      hydrateRoot(el, app)
    } else {
      createRoot(el).render(app)
    }
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages by checking for
  // the presence of the root element (#app by default).
  if (document.getElementById('app')) {
    throw error
  } else {
    console.error(
      'Missing root element.\n\n' +
        'If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n' +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
